import { useAuthUser } from "../../../contexts/AuthUserContext";
import { Icon } from "@iconify/react/dist/iconify.js";
import { useCallData } from "../../../contexts/CallDataContext";
import CallTable from "../../../components/callHistory/callLogs";
import CallDetails from "../../../components/callHistory/callDetails";
import ExportToCSV from "../../../components/callHistory/exportToCSV";
import React, { useState, useEffect } from "react"; 
import { useSearchParams, useNavigate } from "react-router-dom";

import { useSentiment } from "../../../contexts/SentimentContext";
import { useWeek } from "../../../contexts/WeekContext";
import { useCustomer } from "../../../contexts/CustomerContext";
import { startOfDay, endOfDay, isWithinInterval } from "date-fns";
import { parseCustomTimestamp } from "../../../utils/dateUtils";
import { getOrderStatus, getEquipmentList } from "../../../utils/bookingUtils";

const CallHistory = () => {
    const user = useAuthUser();
    const { getEffectiveCustomerId } = useCustomer();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [selectedCall, setSelectedCall] = useState(null);
    const [selectedRows, setSelectedRows] = useState([]);

    // Animation states
    const [animationStates, setAnimationStates] = useState({
        header: false,
        stats: false,
        filters: false,
        table: false
    });

    const { data, loading, error } = useCallData();
    const { sentimentCounts } = useSentiment();
    const { dateRange } = useWeek();
    const [showStatusDropdown, setShowStatusDropdown] = useState(false);

    // Add filter states
    const [activeFilters, setActiveFilters] = useState({
        startTime: null,
        duration: null,
        successEval: null,
        sentiment: null,
        isBookingConfirmed: null,
        multipleEquipment: null,
    });

    // Initialize state from URL parameters on component mount
    useEffect(() => {
        const callId = searchParams.get('id');
        if (callId && data) {
            const call = data.find(c => c.id === callId);
            if (call) {
                setSelectedCall(call);
            }
        }

        // Initialize filters from URL
        const urlFilters = {};
        const filterKeys = ['startTime', 'duration', 'successEval', 'sentiment', 'isBookingConfirmed', 'multipleEquipment'];
        
        filterKeys.forEach(key => {
            const value = searchParams.get(key);
            if (value !== null) {
                if (key === 'isBookingConfirmed' || key === 'multipleEquipment') {
                    urlFilters[key] = value === 'true';
                } else {
                    urlFilters[key] = value;
                }
            }
        });

        if (Object.keys(urlFilters).length > 0) {
            setActiveFilters(prev => ({ ...prev, ...urlFilters }));
            // Set active filter based on what's in URL
            const activeKey = Object.keys(urlFilters).find(key => urlFilters[key] !== null);
            if (activeKey) {
                setActiveFilter(activeKey);
            }
        }
    }, [data, searchParams]);

    // Handle direct URL navigation when component mounts
    useEffect(() => {
        if (data && data.length > 0) {
            const callId = searchParams.get('id');
            if (callId) {
                const call = data.find(c => c.id === callId);
                if (call) {
                    setSelectedCall(call);
                }
            }
        }
    }, [data, searchParams]);

    // Remove this effect since we're manually managing browser history

    // Add this function to filter calls by date range and then by active filters
    const getFilteredCalls = () => {
        if (!data || !dateRange?.start || !dateRange?.end) return [];

        // First, filter by date range
        const rangeStart = startOfDay(dateRange.start);
        const rangeEnd = endOfDay(dateRange.end);

        let filtered = data.filter(call => {
            const callDate = parseCustomTimestamp(call?.metadata?.timestamp);
            if (!callDate) return false;
            return isWithinInterval(callDate, { start: rangeStart, end: rangeEnd });
        });

        // Then, apply active filters
        if (activeFilters.isBookingConfirmed !== null) {
            filtered = filtered.filter(call => {
                const hasBooking = call.booking_exists || call.cart_data;
                const bookingData = call.cart_data || call.booking_info;
                const status = hasBooking ? getOrderStatus(bookingData).toLowerCase() : '';
                const isConfirmed = status === 'confirmed';

                if (activeFilters.isBookingConfirmed) { // true, so we want confirmed
                    return isConfirmed;
                } else { // false, so we want not confirmed
                    return !isConfirmed;
                }
            });
        }

        if (activeFilters.multipleEquipment !== null) {
            filtered = filtered.filter(call => {
                if (!call.booking_exists && !call.cart_data) return false;
                const bookingData = call.cart_data || call.booking_info;
                const equipmentList = getEquipmentList(bookingData);
                const hasMultiple = equipmentList.includes(',');
                return activeFilters.multipleEquipment === hasMultiple;
            });
        }

        // Sorting logic from callLogs.jsx, now in CallHistory.jsx
        if (activeFilters.startTime) {
            filtered.sort((a, b) => {
                const timeA = a.metadata.timestamp;
                const timeB = b.metadata.timestamp;
                return activeFilters.startTime === 'asc' ? timeA.localeCompare(timeB) : timeB.localeCompare(timeA);
            });
        } else {
            // Default sort by timestamp (latest first)
            filtered.sort((a, b) => {
                const timeA = a.metadata.timestamp;
                const timeB = b.metadata.timestamp;
                return timeB.localeCompare(timeA); // This will sort in descending order (latest first)
            });
        }

        if (activeFilters.duration) {
            filtered.sort((a, b) => {
                const durationA = a.analytics.duration || 0;
                const durationB = b.analytics.duration || 0;
                return activeFilters.duration === 'asc' ? durationA - durationB : durationB - durationA;
            });
        }

        if (activeFilters.successEval) {
            filtered.sort((a, b) => {
                const scoreA = a.success_score || 0;
                const scoreB = b.success_score || 0;
                return activeFilters.successEval === 'asc' ? scoreA - scoreB : scoreB - scoreA;
            });
        }

        if (activeFilters.sentiment) {
            filtered.sort((a, b) => {
                const sentimentA = a.analytics.sentiment_analysis.trend || 'neutral';
                const sentimentB = b.analytics.sentiment_analysis.trend || 'neutral';
                
                const ascendingOrder = { 'negative': 1, 'neutral': 2, 'positive': 3 };
                const descendingOrder = { 'positive': 1, 'neutral': 2, 'negative': 3 };

                const order = activeFilters.sentiment === 'asc' ? ascendingOrder : descendingOrder;
                return order[sentimentA] - order[sentimentB];
            });
        }

        return filtered;
    };


    // Use this function where you need filtered call counts
    const filteredCalls = getFilteredCalls();
    const totalFilteredCalls = filteredCalls.length;

    // Trigger staggered animations on component mount
    useEffect(() => {
        const timeouts = [];

        // Stagger the animations with delays
        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, header: true }));
        }, 100));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, stats: true }));
        }, 300));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, filters: true }));
        }, 500));

        timeouts.push(setTimeout(() => {
            setAnimationStates(prev => ({ ...prev, table: true }));
        }, 700));

        // Cleanup timeouts on unmount
        return () => {
            timeouts.forEach(timeout => clearTimeout(timeout));
        };
    }, []);

    // Listen for page reset events from navbar
    useEffect(() => {
        const handlePageReset = (event) => {
            // Check if this page needs to reset based on its current state
            // If we have a selected call, reset to show only logs
            if (selectedCall !== null) {
                setSelectedCall(null);
            }
        };

        window.addEventListener('pageReset', handlePageReset);

        return () => {
            window.removeEventListener('pageReset', handlePageReset);
        };
    }, [selectedCall]);

    // Listen for browser back/forward navigation
    useEffect(() => {
        const handlePopState = () => {
            // Get current URL parameters
            const currentParams = new URLSearchParams(window.location.search);
            const callId = currentParams.get('id');
            
            // Handle call details state
            if (selectedCall && !callId) {
                // Close call details when back button removes ID
                setSelectedCall(null);
            } else if (!selectedCall && callId && data) {
                // Open call details when forward button adds ID
                const call = data.find(c => c.id === callId);
                if (call) {
                    setSelectedCall(call);
                }
            }
            
            // Handle filter changes
            const currentFilters = {};
            const filterKeys = ['startTime', 'duration', 'successEval', 'sentiment', 'isBookingConfirmed', 'multipleEquipment'];
            
            filterKeys.forEach(key => {
                const value = currentParams.get(key);
                if (value !== null) {
                    if (key === 'isBookingConfirmed' || key === 'multipleEquipment') {
                        currentFilters[key] = value === 'true';
                    } else {
                        currentFilters[key] = value;
                    }
                } else {
                    currentFilters[key] = null;
                }
            });
            
            // Update filters
            setActiveFilters(currentFilters);
            
            // Update active filter
            const activeKey = Object.keys(currentFilters).find(key => currentFilters[key] !== null);
            setActiveFilter(activeKey || null);
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [selectedCall, data]);

    // console.log(selectedCall?.metadata.stream_sid);

    // Track the currently active filter
    const [activeFilter, setActiveFilter] = useState(null);

    // Mobile filters state
    const [showMobileFilters, setShowMobileFilters] = useState(false);

    const handleCallSelection = (call) => {
        // Add to browser history when opening call details
        const newParams = new URLSearchParams(searchParams);
        newParams.set('id', call.id);
        window.history.pushState({}, '', `?${newParams.toString()}`);
        setSelectedCall(call);
    };

    const handleCloseCallDetails = () => {
        // Add to browser history when closing call details
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('id');
        window.history.pushState({}, '', `?${newParams.toString()}`);
        setSelectedCall(null);
    };

    // Filter configuration
    const filterConfig = [
        {
            id: 'startTime',
            label: 'Start Time',
            icon: 'stash:data-date'
        },
        {
            id: 'duration',
            label: 'Duration',
            icon: 'mingcute:time-line'
        },
        {
            id: 'sentiment',
            label: 'Sentiment',
            icon: 'material-symbols:add-reaction-outline'
        },
        {
            id: 'successEval',
            label: 'Success',
            icon: 'line-md:check-all'
        },
        {
            id: 'isBookingConfirmed',
            label: 'Confirmed Booking',
            icon: 'material-symbols:check-circle-outline'
        },
        {
            id: 'multipleEquipment',
            label: 'Multiple Equipment',
            icon: 'material-symbols:construction'
        }
    ];

    // Get available status types from orders
    const getAvailableStatusTypes = () => {
        if (!data || data.length === 0) return ['Booking Confirmed', 'Pending', 'Cancelled'];

        const allOrdersData = data.filter(call => call.booking_info);
        const statusTypes = [...new Set(allOrdersData.map(order => getOrderStatus(order.booking_info)))];
        return statusTypes.length > 0 ? statusTypes : ['Booking Confirmed', 'Pending', 'Cancelled'];
    };

    // Handle status selection
    const handleStatusSelect = (status) => {
        setActiveFilters({
            ...activeFilters,
            orderStatus: status,
        });
        setActiveFilter('orderStatus');
        setShowStatusDropdown(false);
    };

    // Handle filter clicks
    const handleFilterClick = (filterName) => {
        let newFilters = { ...activeFilters };
        let newActiveFilter = activeFilter;

        if (filterName === 'startTime' || filterName === 'duration' || filterName === 'successEval' || filterName === 'sentiment') {
            // If clicking the same filter, toggle its direction
            if (activeFilter === filterName) {
                newFilters[filterName] = activeFilters[filterName] === 'asc' ? 'desc' : 'asc';
            } else {
                // If clicking a different filter, deactivate current and activate new one
                newFilters = {
                    startTime: null,
                    duration: null,
                    successEval: null,
                    sentiment: null,
                    isBookingConfirmed: null,
                    multipleEquipment: null,
                    [filterName]: 'asc'
                };
                newActiveFilter = filterName;
            }
        } else if (filterName === 'isBookingConfirmed') {
            if (activeFilter === 'isBookingConfirmed') {
                if (activeFilters.isBookingConfirmed === true) {
                    newFilters.isBookingConfirmed = false;
                } else {
                    newFilters.isBookingConfirmed = null;
                    newActiveFilter = null;
                }
            } else {
                newFilters = {
                    startTime: null,
                    duration: null,
                    successEval: null,
                    sentiment: null,
                    isBookingConfirmed: true,
                    multipleEquipment: null,
                };
                newActiveFilter = 'isBookingConfirmed';
            }
        } else if (filterName === 'multipleEquipment') {
            // Special handling for multiple equipment filter
            if (activeFilter === 'multipleEquipment') {
                // If already filtering, cycle through: multiple -> single -> off
                if (activeFilters.multipleEquipment === true) {
                    newFilters.multipleEquipment = false;
                } else {
                    newFilters.multipleEquipment = null;
                    newActiveFilter = null;
                }
            } else {
                // If not filtering, turn on multiple equipment filter
                newFilters = {
                    startTime: null,
                    duration: null,
                    successEval: null,
                    sentiment: null,
                    isBookingConfirmed: null,
                    multipleEquipment: true
                };
                newActiveFilter = 'multipleEquipment';
            }
        }

        // Update state
        setActiveFilters(newFilters);
        setActiveFilter(newActiveFilter);

        // Add to browser history
        const newParams = new URLSearchParams(searchParams);
        Object.entries(newFilters).forEach(([key, value]) => {
            if (value !== null) {
                newParams.set(key, value.toString());
            } else {
                newParams.delete(key);
            }
        });
        window.history.pushState({}, '', `?${newParams.toString()}`);
    };

    // Reset all filters
    const resetFilters = () => {
        setActiveFilters({
            startTime: null,
            duration: null,
            successEval: null,
            sentiment: null,
            isBookingConfirmed: null,
            multipleEquipment: null,
        });
        setActiveFilter(null);

        // Add to browser history
        const newParams = new URLSearchParams(searchParams);
        Object.keys(activeFilters).forEach(key => {
            newParams.delete(key);
        });
        window.history.pushState({}, '', `?${newParams.toString()}`);
    };

    // Check if any filter is active
    const isAnyFilterActive = Object.values(activeFilters).some(val => val !== null);

    // Get assistant name based on client
    const getAssistantName = () => {
        const clientId = getEffectiveCustomerId();
        if (clientId === 'celeste' || clientId === 'helios') {
            return 'Celeste';
        } else if (clientId === 'wards') {
            return 'Randi';
        } else if (clientId === 'scale') {
            return 'BDR';
        } else if (clientId === 'kronk') {
            return 'Kronk';
        } else {
            // For other clients, use their ID in Title Case
            return clientId ? clientId.charAt(0).toUpperCase() + clientId.slice(1).toLowerCase() : 'Randi';
        }
    };

    // Get assistant phone number based on client
    const getAssistantPhoneNumber = () => {
        const clientId = getEffectiveCustomerId();
        if (clientId === 'wards') {
            return '+12489709365';
        }
        return null; // N/A for celeste/helios and all other clients
    };

    // Filter Button Component
    const FilterButton = ({ id, label, icon }) => {
        return (
            <button
                onClick={() => handleFilterClick(id)}
                className={`flex w-fit whitespace-nowrap flex-row items-center font-medium gap-3 justify-between px-3 py-2 ${activeFilter === id ? 'bg-primary-20 border-primary-30 border' : 'bg-background xl:button-container'} rounded-md text-sm`}
            >
                <span className="text-font-primary flex flex-row items-center gap-1">
                    <Icon icon={icon} className="text-xl text-accent" />
                    {label}
                </span>
                {activeFilter === id && (id === 'startTime' || id === 'duration' || id === 'successEval' || id === 'sentiment') && (
                    <Icon
                        icon={activeFilters[id] === 'asc' ? 'material-symbols:arrow-upward' : 'material-symbols:arrow-downward'}
                        className="text-xl text-accent"
                    />
                )}
                {activeFilter === id && id === 'isBookingConfirmed' && (
                    <Icon
                        icon={activeFilters.isBookingConfirmed === true ? 'material-symbols:check-circle-outline' : 'material-symbols:cancel-outline'}
                        className="text-xl text-accent"
                    />
                )}
                {activeFilter === id && id === 'multipleEquipment' && (
                    <Icon
                        icon={activeFilters.multipleEquipment === true ? 'material-symbols:check-circle-outline' : 'material-symbols:cancel-outline'}
                        className="text-xl text-accent"
                    />
                )}
            </button>
        );
    };

    return (
        <div className={`
            responsive-page-layout scrollable
            h-full max-h-screen overflow-hidden
        `}>
            <div className={`dashboard-header-responsive flex-shrink-0 transition-all duration-700 ease-out ${
                    animationStates.header
                        ? 'opacity-100 translate-y-0'
                        : 'opacity-0 translate-y-4'
                }`}>
                    <div className="flex flex-row gap-2 items-center font-semibold text-xl sm:text-2xl">
                        <Icon icon="material-symbols-light:call-log" className="text-primary text-2xl sm:text-3xl"></Icon>
                        Call History
                    </div>
                    <div className="header-right-3 hidden xl:flex">
                        <div className='flex flex-col sm:flex-row gap-2 w-full lg:w-fit'>
                            <div className="flex flex-row gap-1 items-center text-xs sm:text-sm p-2 rounded-md bg-bgSecondary card-container">
                                <Icon icon="mingcute:ai-fill" className="text-lg sm:text-xl text-accent"></Icon>
                                <p><strong>AI Assistant:</strong> {getAssistantName()}</p>
                            </div>
                            {getAssistantPhoneNumber() && (
                                <div className={`
                                    flex flex-row gap-1 items-center text-xs sm:text-sm p-2 rounded-md button-container
                                    cursor-pointer transition-colors`}
                                    onClick={() => window.location.href = `tel:${getAssistantPhoneNumber()}`}
                                >
                                    <Icon icon="material-symbols:call" className="text-lg sm:text-xl text-accent"></Icon>
                                    <p className="hidden sm:inline"><strong>Assistant Phone Number:</strong> +1 (248) 970 9365</p>
                                    <p className="sm:hidden"><strong>Phone:</strong> +1 (248) 970 9365</p>
                                </div>
                            )}
                        </div>
                    </div>
            </div>
            {/* Main Content - Hidden on small screens when CallDetails is shown */}
            <div className={`
                calls-tab-layout flex-1 flex flex-col gap-4 w-full min-h-0
            `}>
                {/* Summary Data or Breadcrumbs */}
                {!selectedCall ? (
                    <div className={`hidden xl:flex flex-row w-full gap-3 transition-all duration-700 ease-out ${
                        animationStates.stats
                            ? 'opacity-100 translate-y-0 scale-100'
                            : 'opacity-0 translate-y-4 scale-95'
                    }`}>
                        <div className="card-container flex-1 px-2 py-1 sm:px-3 sm:py-2 rounded-md flex justify-between items-center">
                            <div className="flex flex-row items-center gap-2 text-base font-semibold">
                                <Icon icon="material-symbols:call" className="text-xl text-yellow-400"></Icon>
                                Total Calls
                            </div>
                            <p className="text-yellow-400 font-semibold text-lg">{filteredCalls.length}</p>
                        </div>
                        <div className="card-container flex-1 px-2 py-1 sm:px-3 sm:py-2 rounded-md flex justify-between items-center">
                            <div className="flex flex-row items-center gap-2 text-base font-semibold">
                                <Icon icon="icon-park-outline:positive-dynamics" className="text-xl text-green-500"></Icon>
                                Improving
                            </div>
                            <p className="text-green-500 font-semibold text-lg">{sentimentCounts.improving}</p>
                        </div>
                        <div className="card-container flex-1 px-2 py-1 sm:px-3 sm:py-2 rounded-md flex justify-between items-center">
                            <div className="flex flex-row items-center gap-2 text-base font-semibold">
                                <Icon icon="icon-park-outline:negative-dynamics" className="text-xl text-accent"></Icon>
                                Declining
                            </div>
                            <p className="text-accent font-semibold text-lg">{sentimentCounts.declining}</p>
                        </div>
                    </div>
                ) : (
                    <div className="flex items-center gap-1 text-font-primary">
                        <button
                            onClick={handleCloseCallDetails}
                            className="flex items-center justify-center rounded-lg hover:bg-bgAccent transition-colors cursor-pointer"
                            title="Go back to call history"
                        >
                            <Icon icon="mdi:chevron-left" className="text-3xl text-accent hover:text-primary transition-colors" />
                        </button>
                        <span 
                            className="flex items-center justify-center gap-1 group transition-colors cursor-pointer"
                            onClick={handleCloseCallDetails}
                        >
                            <Icon icon="material-symbols-light:call-log" className="text-xl text-font-secondary group-hover:text-primary transition-colors" />
                            <span className="text-font-secondary group-hover:text-font-primary transition-colors">Call History</span>
                        </span>
                        <Icon icon="mdi:chevron-right" className="text-lg text-font-secondary" />
                        <span className="flex items-center justify-center gap-1">
                            <Icon icon="material-symbols:info-outline" className="text-2xl text-primary" />
                            <span className="text-font-primary text-base">
                                Call Details
                            </span>
                        </span>
                    </div>
                )}
                {/* New layout for Call History */}
                <div className="flex flex-1 gap-2 w-full min-h-0">
                    <div className={`
                        flex flex-col w-full gap-4 min-w-0 min-h-0 transition-all duration-300 ease-out
                        ${selectedCall ? 'hidden 2xl:flex 2xl:w-full' : 'flex'}
                        ${animationStates.filters
                            ? 'opacity-100 translate-y-0'
                            : 'opacity-0 translate-y-4'
                        }
                    `}>
                        {/* Filters */}
                        <div className={`w-full flex flex-row items-start sm:items-center ${user.role === 'helios_admin' ? 'justify-between' : 'sm:justify-start'} gap-2`}>

                            {/* Desktop Filters (md and above) */}
                            <div className={`call-filters hidden xl:flex flex-row gap-2 items-center w-fit ${selectedCall ? '2xl:hidden' : ''}`}>
                                {filterConfig.map(filter => (
                                    <FilterButton
                                        key={filter.id}
                                        id={filter.id}
                                        label={filter.label}
                                        icon={filter.icon}
                                    />
                                ))}
                                {isAnyFilterActive && (
                                    <button
                                        onClick={resetFilters}
                                        className="flex flex-row items-center gap-2 p-2 button-container whitespace-nowrap text-sm ml-auto rounded-md transition-colors"
                                    >
                                        <Icon icon="material-symbols:refresh" className="text-xl text-accent" />
                                        Reset Filters
                                    </button>
                                )}
                            </div>

                            {/* Mobile All Filters Button (below md) */}
                            <div className={`w-fit relative xl:hidden ${selectedCall ? '2xl:block' : ''}`}>
                                <button
                                    onClick={() => setShowMobileFilters(!showMobileFilters)}
                                    className="flex flex-row items-center gap-2 p-2 button-container text-sm rounded-md transition-colors w-full justify-center"
                                >
                                    <Icon icon="material-symbols:filter-list" className="text-xl text-accent" />
                                    All Filters
                                    {isAnyFilterActive && (
                                        <span className="bg-primary text-white text-xs px-2 py-1 rounded-full ml-2">
                                            {Object.values(activeFilters).filter(Boolean).length}
                                        </span>
                                    )}
                                </button>

                                {/* Mobile Filters Dropdown */}
                                {showMobileFilters && (
                                    <div className="absolute top-full w-fit left-0 right-0 mt-2 button-container rounded-md shadow-lg border border-bgAccent z-50">
                                        <div className="flex flex-col ">
                                            {filterConfig.map(filter => (
                                                <FilterButton
                                                    key={filter.id}
                                                    id={filter.id}
                                                    label={filter.label}
                                                    icon={filter.icon}
                                                />
                                            ))}
                                            {isAnyFilterActive && (
                                                <button
                                                    onClick={() => {
                                                        resetFilters();
                                                        setShowMobileFilters(false);
                                                    }}
                                                    className="flex flex-row items-center gap-2 p-2 text-sm rounded-md transition-colors justify-center mt-2 border-t border-border"
                                                >
                                                    <Icon icon="material-symbols:refresh" className="text-xl text-error" />
                                                    Reset Filters
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Click outside to close mobile filters */}
                                {showMobileFilters && (
                                    <div
                                        className="fixed inset-0 z-40"
                                        onClick={() => setShowMobileFilters(false)}
                                    />
                                )}
                            </div>

                            {user.role === 'helios_admin' && (
                                <div className="w-fit sm:w-auto">
                                    <ExportToCSV data={data} selectedCall={selectedCall} selectedRows={selectedRows} />
                                </div>
                            )}
                        </div>
                        {/* Call Logs */}
                        <div className="flex flex-1 gap-2 w-full min-h-0">
                            {loading && <div className="p-4 w-full text-center text-gray-400">Loading call history...</div>}
                            {error && <div className="p-4 w-full text-center text-error">Error loading call history: {error.message}</div>}
                            
                            {/* Only render the table when not loading and no error */}
                            {!loading && !error && (
                                <CallTable
                                    onRowClick={handleCallSelection}
                                    isShort={!!selectedCall}
                                    callRow={selectedCall?.metadata.stream_sid}
                                    displayData={filteredCalls}
                                    selectedRows={selectedRows}
                                    setSelectedRows={setSelectedRows}
                                    className={`w-full h-full flex-1 min-h-0 transition-all duration-700 ease-out ${
                                        animationStates.table
                                            ? 'opacity-100 translate-y-0'
                                            : 'opacity-0 translate-y-4'
                                    }`}
                                />
                            )}
                        </div>
                    </div>
                    {/* The CallDetails content will only render when a call is selected */}
                    {selectedCall && (
                        <div className="w-full flex flex-col 2xl:w-1/2 flex-shrink-0 min-h-0 max-h-screen overflow-hidden">
                            <CallDetails call={selectedCall} onClose={handleCloseCallDetails} />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CallHistory;

